// FILE: src/utils/phaseAnalytics.js
//
// Pure utility functions for grouping exam results by a categorical dimension
// (called "phase" here — maps to category for CPMAI and domain for PMP).
//
// ── Phase / Domain config ─────────────────────────────────────────────────────
//
//   PHASE_CONFIG maps examId → ordered array of phase descriptors.
//   Each descriptor provides:
//     key         exact string that appears in question.category
//     label       short display name shown on charts
//     description longer text for the insights panel
//
//   Questions whose category does not match any configured key fall into an
//   "Uncategorized" bucket that is always appended last.
//
// ── Output shape (PhaseMetric) ────────────────────────────────────────────────
//
//   {
//     key           string   — matches question.category (or '__uncategorized__')
//     label         string   — human-readable name
//     description   string   — topic description for insights text
//     total         number   — total questions in this phase
//     correct       number
//     incorrect     number
//     notAnswered   number   — skipped + unanswered combined
//     pct           number   — Math.round(correct / total * 100), or 0 if total=0
//     hasData       boolean  — true when total > 0
//   }

// ── Per-exam configuration ────────────────────────────────────────────────────

export const PHASE_CONFIG = {
  cpmai: [
    {
      key: 'Business Understanding',
      label: 'Phase I',
      description: 'business understanding, problem framing, and project scoping',
    },
    {
      key: 'Data Understanding',
      label: 'Phase II',
      description: 'data exploration, profiling, and quality assessment',
    },
    {
      key: 'Data Preparation',
      label: 'Phase III',
      description: 'data cleaning, feature engineering, and transformation',
    },
    {
      key: 'Model Development',
      label: 'Phase IV',
      description: 'algorithm selection, training, and hyperparameter tuning',
    },
    {
      key: 'Model Evaluation',
      label: 'Phase V',
      description: 'model testing, validation, and performance benchmarking',
    },
    {
      key: 'Model Operationalization',
      label: 'Phase VI',
      description: 'deployment, monitoring, and model lifecycle management',
    },
  ],
  pmp: [
    {
      key: 'Initiating',
      label: 'Initiating',
      description: 'project initiation, charters, and stakeholder identification',
    },
    {
      key: 'Planning',
      label: 'Planning',
      description: 'scope, schedule, budget, risk, and communications planning',
    },
    {
      key: 'Execution',
      label: 'Executing',
      description: 'team management, quality assurance, and deliverable production',
    },
    {
      key: 'Monitoring and Controlling',
      label: 'Monitoring',
      description: 'performance tracking, change control, and variance analysis',
    },
    {
      key: 'Closing',
      label: 'Closing',
      description: 'project closure, lessons learned, and final deliverables',
    },
  ],
  // PMI-ACP — 2026 four-domain ECO. Labels are already short; included so the
  // pie legend + "Domain Key" panel render consistently (not the generic fallback).
  'pmi-acp': [
    { key: 'Mindset',    label: 'Mindset',    description: 'agile mindset, principles, and servant leadership' },
    { key: 'Leadership', label: 'Leadership', description: 'team facilitation, stakeholder engagement, and coaching' },
    { key: 'Product',    label: 'Product',    description: 'value-driven delivery, backlog, and prioritization' },
    { key: 'Delivery',   label: 'Delivery',   description: 'flow, cadence, WIP limits, and continuous improvement' },
  ],
  // PMI-PMOCP — six ECO domains. SHORT labels keep the pie legend compact so it
  // no longer overlaps the chart (long full names triggered the generic fallback).
  'pmi-pmocp': [
    { key: 'Organizational Development and Alignment', label: 'Org Dev',     description: 'aligning the PMO to enterprise strategy and value' },
    { key: 'PMO Strategic Elements',                   label: 'Strategy',    description: 'governance, intake, and portfolio strategy' },
    { key: 'PMO Design and Structuring',               label: 'Design',      description: 'PMO operating model, structure, and services' },
    { key: 'People',                                   label: 'People',      description: 'stakeholder engagement, leadership, and competency' },
    { key: 'PMO Operations and Performance',           label: 'Operations',  description: 'operating cadences, KPIs, and performance reporting' },
    { key: 'PMO Enhancement and Effectiveness',        label: 'Enhancement', description: 'maturity, innovation, and continuous improvement' },
  ],
};

const UNCATEGORIZED = {
  key: '__uncategorized__',
  label: 'Other',
  description: 'questions without a category assignment',
};

// ── computePhaseMetrics ───────────────────────────────────────────────────────

/**
 * Groups result items by phase/category and computes per-group statistics.
 *
 * @param {object[]} resultItems
 *   Array of result records produced by computeResults() in ResultsPage.js.
 *   Each item: { question: { category, ... }, status: 'correct'|'incorrect'|'skipped'|'unanswered' }
 *
 * @param {string} examId
 *   e.g. 'cpmai' or 'pmp'. Determines which PHASE_CONFIG entry to use.
 *   Falls back to a generic grouping if the examId is not configured.
 *
 * @returns {PhaseMetric[]}
 *   Ordered array of phase metrics. Phases with zero questions are included
 *   (hasData = false) so the chart axes stay consistent across attempts.
 *   The uncategorized bucket is appended only when it contains questions.
 */
export function computePhaseMetrics(resultItems, examId) {
  const config = PHASE_CONFIG[examId];

  if (!config) {
    // Unknown exam — fall back to grouping by raw category value
    return computeGenericMetrics(resultItems);
  }

  // Build a lookup for configured phases
  const buckets = new Map(
    config.map((p) => [
      p.key,
      { ...p, total: 0, correct: 0, incorrect: 0, notAnswered: 0 },
    ])
  );

  // Accumulate counts; unconfigured categories go to overflow
  const overflow = { ...UNCATEGORIZED, total: 0, correct: 0, incorrect: 0, notAnswered: 0 };

  for (const item of resultItems) {
    const cat = item.question.category ?? '';
    const bucket = buckets.get(cat) ?? overflow;
    if (!buckets.has(cat)) {
      // assign to overflow
      overflow.total += 1;
      if (item.status === 'correct')   overflow.correct   += 1;
      else if (item.status === 'incorrect') overflow.incorrect += 1;
      else                             overflow.notAnswered += 1;
      continue;
    }
    bucket.total += 1;
    if (item.status === 'correct')        bucket.correct    += 1;
    else if (item.status === 'incorrect') bucket.incorrect  += 1;
    else                                  bucket.notAnswered += 1;
  }

  const results = config.map((p) => {
    const b = buckets.get(p.key);
    return finalise(b);
  });

  if (overflow.total > 0) {
    results.push(finalise(overflow));
  }

  return results;
}

// ── computeGenericMetrics (fallback) ─────────────────────────────────────────

function computeGenericMetrics(resultItems) {
  const buckets = new Map();

  for (const item of resultItems) {
    const cat = item.question.category?.trim() || '__uncategorized__';
    if (!buckets.has(cat)) {
      const label = cat === '__uncategorized__' ? 'Other' : cat;
      buckets.set(cat, { key: cat, label, description: label, total: 0, correct: 0, incorrect: 0, notAnswered: 0 });
    }
    const b = buckets.get(cat);
    b.total += 1;
    if (item.status === 'correct')        b.correct    += 1;
    else if (item.status === 'incorrect') b.incorrect  += 1;
    else                                  b.notAnswered += 1;
  }

  return Array.from(buckets.values()).map(finalise);
}

// ── Helpers ───────────────────────────────────────────────────────────────────

function finalise(b) {
  const pct = b.total > 0 ? Math.round((b.correct / b.total) * 100) : 0;
  return { ...b, pct, hasData: b.total > 0 };
}

// ── generateInsights ──────────────────────────────────────────────────────────

/**
 * Derives 2–4 human-readable insight strings from phase metrics.
 *
 * @param   {PhaseMetric[]} metrics   Output of computePhaseMetrics()
 * @returns {string[]}                Array of insight sentences
 */
export function generateInsights(metrics) {
  const withData = metrics.filter((m) => m.hasData);
  if (withData.length === 0) return [];

  const insights = [];

  // Best phase
  const best = [...withData].sort((a, b) => b.pct - a.pct)[0];
  if (best.pct >= 70) {
    insights.push(
      `Strong performance in ${best.label} (${best.pct}%) — keep it up.`
    );
  }

  // Weakest phase
  const worst = [...withData].sort((a, b) => a.pct - b.pct)[0];
  if (worst.pct < 70 && worst !== best) {
    insights.push(
      `${worst.label} needs attention (${worst.pct}%) — review ${worst.description}.`
    );
  } else if (worst.pct < 70) {
    insights.push(
      `${worst.label} needs attention (${worst.pct}%) — review ${worst.description}.`
    );
  }

  // Most missed (by raw incorrect count)
  const mostMissed = [...withData].sort((a, b) => b.incorrect - a.incorrect)[0];
  if (mostMissed.incorrect > 0 && mostMissed !== worst) {
    insights.push(
      `Most missed questions came from ${mostMissed.label} (${mostMissed.incorrect} incorrect).`
    );
  }

  // Overall encouragement or warning
  const avgPct = Math.round(withData.reduce((s, m) => s + m.pct, 0) / withData.length);
  if (avgPct >= 80) {
    insights.push('Overall phase coverage is strong — you are well-prepared.');
  } else if (avgPct < 50) {
    insights.push('Broad review across all phases is recommended before attempting the exam.');
  }

  return insights;
}

// ── getWeakPhases ─────────────────────────────────────────────────────────────

/**
 * Minimum number of answered (correct + incorrect) questions required for a
 * phase to be considered when identifying weak areas.  Phases with fewer
 * answered questions are ignored — too little signal.
 */
export const MIN_ANSWERED_FOR_WEAK = 3;

/**
 * Identifies the 1–2 weakest phases from a set of phase metrics.
 *
 * Selection rules (applied in order):
 *   1. Only consider phases with hasData = true AND
 *      (correct + incorrect) >= MIN_ANSWERED_FOR_WEAK.
 *   2. Rank remaining phases: lowest pct first; ties broken by highest
 *      incorrect count.
 *   3. Return the top 1–2 weakest phases, but only those scoring < 80%.
 *      (Phases at or above 80% are not considered weak.)
 *
 * Returns an empty array when:
 *   - No phases have sufficient data, OR
 *   - All qualifying phases score >= 80%
 *
 * @param   {PhaseMetric[]} metrics  Output of computePhaseMetrics()
 * @returns {PhaseMetric[]}          0–2 weakest phase metrics, worst first
 */
export function getWeakPhases(metrics) {
  const PASS_THRESHOLD = 80;

  const eligible = metrics.filter(
    (m) => m.hasData && (m.correct + m.incorrect) >= MIN_ANSWERED_FOR_WEAK && m.pct < PASS_THRESHOLD
  );

  if (eligible.length === 0) return [];

  const ranked = [...eligible].sort((a, b) => {
    if (a.pct !== b.pct) return a.pct - b.pct;       // lowest % first
    return b.incorrect - a.incorrect;                  // most incorrect first (tie-break)
  });

  return ranked.slice(0, 2);
}
